import React from 'react';
import { Music, Users, DollarSign, Store } from 'lucide-react';
import { StatsCard } from '../components/dashboard/StatsCard';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { QuickActions } from '../components/dashboard/QuickActions';
import { DistributionStatus } from '../components/dashboard/DistributionStatus';
import { allStores } from '../data/mockData';
import { useAppData } from '../context/DataContext';

interface DashboardProps {
  onTabChange: (tab: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onTabChange }) => {
  const { songs, artists, user } = useAppData();
  const totalStreams = songs.reduce((sum, song) => sum + song.streams, 0);
  const totalRevenue = songs.reduce((sum, song) => sum + song.revenue, 0);
  const totalStores = allStores.length; // From our mock data (478 stores)
  
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };
  
  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };
  
  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white font-display mb-2">
          Welcome back, {user.name.split(' ')[0]}! 👋
        </h2>
        <p className="text-gray-400">
          Here's what's happening with your music distribution today.
        </p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Songs"
          value={songs.length}
          icon={Music}
          change={{ value: 12, type: 'increase' }}
          color="gold"
        />
        <StatsCard
          title="Active Artists"
          value={artists.length}
          icon={Users}
          change={{ value: 8, type: 'increase' }}
          color="purple"
        />
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          icon={DollarSign}
          change={{ value: 15, type: 'increase' }}
          color="green"
        />
        <StatsCard
          title="Distribution Stores"
          value={totalStores}
          icon={Store}
          color="blue"
        />
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          <QuickActions onTabChange={onTabChange} />
          <DistributionStatus />
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          <RecentActivity />
        </div>
      </div>
    </div>
  );
};